import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Modal, Form, Breadcrumb } from "react-bootstrap";
import { getToken } from "../../utils/auth";

const SliderManager = ({ setIsAuthPage }) => {
  const [images, setImages] = useState([]); // Start with empty, will fetch from API
  const [showModal, setShowModal] = useState(false);
  const [newImage, setNewImage] = useState({
    source: "",
    sequence: images.length + 1,
    category: "",
    file: null,
  });
  const [editedImages, setEditedImages] = useState([]);
  const [imagesWithBase64, setImagesWithBase64] = useState([]);

  // Category management states
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoryError, setCategoryError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    if (setIsAuthPage) setIsAuthPage(false);
    setEditedImages(images.map((img) => ({ ...img })));
  }, [setIsAuthPage, images]);

  // Helper to get Authorization headers (reuse if needed)
  const getAuthHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch categories on mount
  useEffect(() => {
    async function fetchCategories() {
      setLoadingCategories(true);
      setCategoryError(null);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/topic/get-topic-categories`,
          {
            headers: {
              ...getAuthHeaders(),
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch categories");
        
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          const text = await response.text();
          throw new Error("Server error: " + text);
        }
        
        const data = await response.json();
        setCategories(data);
      } catch (err) {
        setCategoryError(err.message);
      } finally {
        setLoadingCategories(false);
      }
    }

    fetchCategories();
  }, []);

  // Fetch slider images from backend API
  const fetchImages = useCallback(async (category = selectedCategory) => {
    try {
      let url = `${process.env.REACT_APP_API_BASE_URL}/topic-image/get-topic-image-by-category`;
      let options = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({}),
      };
      if (category !== "all") {
        options.body = JSON.stringify({ RowIdTopicCategory: category });
      }
      const response = await fetch(url, options);
      if (!response.ok) throw new Error("Failed to fetch images");
      
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        throw new Error("Server error: " + text);
      }
      
      const data = await response.json();
      const mapped = data.map((img) => ({
        rowID: img.rowID,
        title: img.source,
        source: img.source,
        sequence: img.sequence || 1,
        category: img.rowidtopiccategory,
      }));
      setImages(mapped);
    } catch (err) {
      setImages([]);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchImages(selectedCategory);
  }, [selectedCategory, fetchImages]);

  useEffect(() => {
    async function convertImagesToBase64() {
      const imageBaseUrl = process.env.REACT_APP_API_BASE_URL;
      const promises = images.map(async (img) => {
        try {
          // Remove /api from base URL if present
          let baseUrl = imageBaseUrl.replace(/\/api$/, "");
          const response = await fetch(baseUrl + "/" + img.source);
          const blob = await response.blob();
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve({
                ...img,
                base64: reader.result,
              });
            };
            reader.readAsDataURL(blob);
          });
        } catch {
          return {
            ...img,
            base64: null,
          };
        }
      });
      const results = await Promise.all(promises);
      setImagesWithBase64(results);
    }

    if (images.length > 0) {
      convertImagesToBase64();
    } else {
      setImagesWithBase64([]);
    }
  }, [images]);

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    setNewImage({
      source: "",
      sequence: images.length + 1,
      category: "",
      file: null,
    });
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "file") {
      setNewImage({
        ...newImage,
        file: files[0] || null,
      });
    } else {
      setNewImage({
        ...newImage,
        [name]: value,
      });
    }
  };

  const handleAddImage = async () => {
    if (!newImage.file) {
      alert("Please select an image file.");
      return;
    }
    const formData = new FormData();
    formData.append("Sequence", newImage.sequence);
    formData.append("Category", newImage.category);
    formData.append("ImageFile", newImage.file);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/topic-image/upload`,
        {
          method: "POST",
          headers: {
            ...getAuthHeaders(),
          },
          body: formData,
        }
      );
      if (!response.ok) throw new Error("Failed to upload image");
      // Re-fetch images after successful upload
      await fetchImages(selectedCategory);
      setShowModal(false);
      setNewImage({
        source: "",
        sequence: images.length + 1,
        category: "",
        file: null,
      });
    } catch (err) {
      alert("Error uploading image: " + err.message);
    }
  };

  const handleDelete = (id) => {
    setImages(images.filter((img) => img.rowID !== id));
  };

  const handleSequenceChange = (id, value) => {
    setImages(
      images.map((img) =>
        img.rowID === id
          ? {
              ...img,
              sequence: Number(value),
            }
          : img
      )
    );
  };

  const handleSaveSequence = () => {
    // Sort by sequence and update images
    const sorted = [...editedImages].sort((a, b) => a.sequence - b.sequence);
    setImages(sorted);

    // Call backend API to update sequence
    const updateSequence = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/topic-image/update-sequence`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...getAuthHeaders(),
            },
            body: JSON.stringify(sorted),
          }
        );
        const result = await response.json();
        if (result.success) {
          // Optionally show a success message or refresh images
        } else {
          // Handle error
        }
      } catch (error) {
        // Handle network error
      }
    };
    updateSequence();
  };

  // Always show images for the selected category

  const filteredImages = imagesWithBase64;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
        <h3 className="mb-0">Slider Image Manager</h3>
        <Breadcrumb className="breadcrumb-page-list align-items-center mb-0 lh-1">
          <Breadcrumb.Item href="/dashboard/ecommerce">
            <div className="d-flex align-items-center text-decoration-none">
              <i className="ri-home-4-line fs-18 text-primary me-1"></i>
              <span className="text-secondary fw-medium hover">Dashboard</span>
            </div>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span className="fw-medium">Slider</span>
          </Breadcrumb.Item>
          <Breadcrumb.Item active>
            <span className="fw-medium">Manage Images</span>
          </Breadcrumb.Item>
        </Breadcrumb>
      </div>
      {/* Category filter */}
      <div className="px-4 pb-2 col-3">
        <Form.Group className="mb-4">
          <label className="label text-secondary">Danh mục</label>
          {loadingCategories ? (
            <div>Tải danh mục...</div>
          ) : categoryError ? (
            <div className="text-danger">{categoryError}</div>
          ) : (
            <Form.Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="all">Tất cả các loại</option>
              {categories
                .filter((c) => !c.isDeleted)
                .map((cat) => (
                  <option key={cat.rowID} value={cat.rowID}>
                    {cat.name}
                  </option>
                ))}
            </Form.Select>
          )}
        </Form.Group>
      </div>
      <Button variant="primary" onClick={handleShowModal} className="mb-3">
        Add Image
      </Button>
      <Button
        variant="success"
        onClick={handleSaveSequence}
        className="mb-3 ms-2"
      >
        Save
      </Button>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Sequence</th>
            <th>Image</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredImages
            .slice()
            .sort((a, b) => a.sequence - b.sequence)
            .map((img, idx) => (
              <tr key={img.rowID || img.rowid || img.source || idx}>
                <td>
                  <Form.Control
                    type="number"
                    value={img.sequence}
                    min={1}
                    style={{ width: 80 }}
                    onChange={(e) =>
                      handleSequenceChange(img.rowID, e.target.value)
                    }
                  />
                </td>
                <td>
                  {img.base64 ? (
                    <img
                      src={img.base64}
                      alt={img.source}
                      style={{ width: 100 }}
                    />
                  ) : (
                    <span>Image not available</span>
                  )}
                </td>
                <td>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(img.rowID)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
        </tbody>
      </Table>
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Add Slider Image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Title field removed */}
            <Form.Group className="mb-3">
              <Form.Label>Image File</Form.Label>
              <Form.Control
                type="file"
                name="file"
                accept="image/*"
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Sequence</Form.Label>
              <Form.Control
                type="number"
                name="sequence"
                value={newImage.sequence}
                min={1}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select
                name="category"
                value={newImage.category || ""}
                onChange={handleChange}
              >
                <option value="">Chọn danh mục</option>
                {categories
                  .filter((c) => !c.isDeleted)
                  .map((cat) => (
                    <option key={cat.rowID} value={cat.rowID}>
                      {cat.name}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddImage}>
            Add
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SliderManager;
